/**
 * Channels list page — (tf3-35a; B-F14) the owner's connected channels on every platform, from the package's store (the
 * same list Settings shows), each with its status and, when it needs one, the reconnect prompt. Connecting is on Settings.
 */
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { channelsApi, type Channel } from "@/api/channels.ts";
import { PageContainer, SectionHeader, TfCard, EmptyState, Btn, TfSpinner } from "@/components/v2/primitives.tsx";
import { channelPath, platformOf, statusLine, NO_CHANNELS } from "@/lib/package-channels.ts";

export function ChannelsListPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({ queryKey: ["publishing", "channels"], queryFn: () => channelsApi.list() });
  const channels = data?.channels ?? [];
  const toSettings = () => navigate("/settings");

  if (isLoading) {
    return (
      <PageContainer title="Channels">
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><TfSpinner size={32} /></div>
      </PageContainer>
    );
  }
  if (error) {
    return <PageContainer title="Channels"><TfCard><p style={{ color: "var(--tf-danger, #dc2626)", margin: 0 }}>Your channels couldn't be read: {(error as Error).message}</p></TfCard></PageContainer>;
  }
  if (channels.length === 0) {
    return (
      <PageContainer title="Channels">
        <TfCard padding={40}>
          <EmptyState icon="folder" title="No channels connected" sub={NO_CHANNELS}
            action={<Btn variant="primary" icon="plus" onClick={toSettings}>Connect a channel</Btn>} />
        </TfCard>
      </PageContainer>
    );
  }
  return (
    <PageContainer title="Channels" subtitle="Your connected YouTube, TikTok and Instagram accounts"
      action={<Btn variant="primary" icon="plus" onClick={toSettings}>Connect a channel</Btn>} maxWidth={1280}>
      <SectionHeader title="Your channels" subtitle="Open a channel for what you posted there and its numbers" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
        {channels.map((c) => <ChannelCard key={c.channelId} channel={c} onOpen={() => navigate(channelPath(c.channelId))} />)}
      </div>
    </PageContainer>
  );
}

function ChannelCard({ channel, onOpen }: { channel: Channel; onOpen: () => void }) {
  const line = statusLine(channel);
  return (
    <button onClick={onOpen} style={{ background: "var(--tf-bg)", border: "1px solid var(--tf-border)", borderRadius: 12, cursor: "pointer",
      textAlign: "left", font: "inherit", color: "inherit", padding: 16, display: "flex", gap: 12, alignItems: "center" }}>
      {channel.avatarUrl
        ? <img src={channel.avatarUrl} alt="" width={44} height={44} style={{ borderRadius: 22, objectFit: "cover" }} />
        : <div style={{ width: 44, height: 44, borderRadius: 22, background: "var(--tf-surface)" }} />}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{channel.accountName}</div>
        <div style={{ fontSize: 12, color: "var(--tf-fg-muted)" }}>{platformOf(channel.channel)}</div>
        <div role="status" style={{ fontSize: 12, marginTop: 4, color: line.reconnect ? "var(--tf-warning, #d97706)" : "var(--tf-success, #16a34a)" }}>{line.text}</div>
      </div>
    </button>
  );
}
